import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface HomepageImage {
  id: string;
  image_url: string;
  top_pos: string | null;
  left_pos: string | null;
  right_pos: string | null;
  bottom_pos: string | null;
  rotation: string;
  width_val: string;
  height_val: string;
  z_index_val: number;
}

export function useHomepageImageManagement() {
  const [images, setImages] = useState<HomepageImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from('homepage_images')
        .select('*')
        .order('created_at', { ascending: true });

      if (supabaseError) throw supabaseError;
      setImages(data || []);
    } catch (err: any) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const addImage = async (imageFile: File, imageData: Omit<HomepageImage, 'id' | 'image_url'>) => {
    try {
      setLoading(true);
      setError(null);

      // Upload image to storage
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `homepage/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('homepage_images')
        .upload(filePath, imageFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('homepage_images')
        .getPublicUrl(filePath);

      // Insert record with public URL
      const { error: insertError } = await supabase
        .from('homepage_images')
        .insert([{
          ...imageData,
          image_url: publicUrl
        }]);

      if (insertError) throw insertError;
      await fetchImages();
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateImage = async (id: string, data: Partial<HomepageImage>) => {
    try {
      setLoading(true);
      setError(null);

      const { error: updateError } = await supabase
        .from('homepage_images')
        .update(data)
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchImages();
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteImage = async (id: string, imageUrl: string) => {
    try {
      setLoading(true);
      setError(null);

      // Extract filename from URL
      const fileName = imageUrl.split('/').pop();
      if (fileName) {
        // Delete from storage
        const { error: storageError } = await supabase.storage
          .from('homepage_images')
          .remove([`homepage/${fileName}`]);

        if (storageError) throw storageError;
      }

      // Delete from database
      const { error: deleteError } = await supabase
        .from('homepage_images')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchImages();
    } catch (err: any) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    images,
    loading,
    error,
    addImage,
    updateImage,
    deleteImage
  };
}